import { useCallback, useEffect, useRef } from "react";
import { extraTheme } from "domains/theme";

export function useItemTabIndicator({
  containerRef,
  tabIndicatorRef,
  activeItemDataId,
}: {
  containerRef: React.RefObject<HTMLDivElement>;
  tabIndicatorRef: React.RefObject<HTMLDivElement>;
  activeItemDataId: string | undefined;
}) {
  const previousActiveItemDataId = useRef<string | undefined>(undefined);

  const fadeOut = useCallback(() => {
    if (tabIndicatorRef.current) {
      tabIndicatorRef.current.style.transition = "unset";
      tabIndicatorRef.current.style.opacity = "1";
      tabIndicatorRef.current.style.transition = extraTheme.transitions.fast;
      tabIndicatorRef.current.style.opacity = "0";
    }
  }, [tabIndicatorRef]);

  const move = useCallback(
    (withTransition = true) => {
      if (!activeItemDataId) {
        return;
      }

      const activeIndicator = tabIndicatorRef.current;
      const activeTab = containerRef.current?.querySelector(
        `[data-id='${activeItemDataId}']`
      ) as HTMLButtonElement;
      if (activeIndicator && activeTab) {
        activeIndicator.style.transition = withTransition
          ? extraTheme.transitions.fast
          : "none";
        activeIndicator.style.left = `${activeTab.offsetLeft}px`;
        activeIndicator.style.width = `${activeTab.offsetWidth}px`;
        activeIndicator.style.height = `${activeTab.offsetHeight}px`;
        activeIndicator.style.top = `${activeTab.offsetTop}px`;
      }
    },
    [activeItemDataId, containerRef, tabIndicatorRef]
  );

  const fadeIn = useCallback(() => {
    if (tabIndicatorRef.current) {
      move(false);
      tabIndicatorRef.current.style.transition = extraTheme.transitions.fast;
      tabIndicatorRef.current.style.opacity = "1";
    }
  }, [move, tabIndicatorRef]);

  useEffect(() => {
    let isMoving = false;
    if (activeItemDataId !== previousActiveItemDataId.current) {
      if (!activeItemDataId) {
        fadeOut();
      } else if (!previousActiveItemDataId.current) {
        fadeIn();
      } else {
        move();
        isMoving = true;
      }
      previousActiveItemDataId.current = activeItemDataId;
    }

    if (activeItemDataId) {
      let resizeObserver: ResizeObserver | undefined;
      const timeout = setTimeout(
        () => {
          if (containerRef.current) {
            resizeObserver = new ResizeObserver(() => {
              move(false);
            });
            resizeObserver.observe(containerRef.current);
          }
        },
        isMoving ? 500 : 0
      );

      return () => {
        clearTimeout(timeout);
        resizeObserver?.disconnect();
      };
    }
  }, [activeItemDataId, containerRef, fadeIn, fadeOut, move]);
}
